import {
  AppBar, Avatar, Box, ButtonBase, Divider, IconButton, ListItemIcon, Menu, MenuItem, Stack, ToggleButton,
  ToggleButtonGroup, Toolbar, Typography, useTheme,
} from '@mui/material';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded';
import ContrastRoundedIcon from '@mui/icons-material/ContrastRounded';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AllAppsButton } from './AllAppsButton';
import { Logo } from './Logo';
import { ModuleSwitcher } from './ModuleSwitcher';
import { NotificationsMenu } from './NotificationsMenu';
import { OrganizationMenu } from './OrganizationMenu';
import { RouteBar } from './RouteBar';
import { useActiveModule } from './useActiveModule';
import { endEdge } from './menuOrigin';
import { useAuthStore } from '@/store/auth';
import { usePreferencesStore, type ThemeMode } from '@/store/preferences';
import { LOCALES, LOCALE_LABELS, type Locale } from '@/i18n/locales';
import { initials } from '@/lib/format';
import { layout } from '@/theme/tokens';
import { paths } from '@/routes/paths';

/**
 * The same bar in every module and on every page: the wordmark, the way back
 * to the apps grid and the module switcher on the inline start; on the end,
 * the chrome that belongs to the person rather than to a module —
 * notifications, the organization they act as, and their own menu, which is
 * also where language and theme change (Shell board): one place, no trip to
 * Settings.
 */
export function Topbar({ onMenuClick }: { onMenuClick: () => void }) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const isPlatform = useAuthStore((s) => s.isPlatform);
  const { hasSidebar } = useActiveModule();
  const [userMenu, setUserMenu] = useState<HTMLElement | null>(null);

  return (
    <AppBar
      position="sticky"
      sx={{
        top: 0,
        // Fixed, border included (border-box), so the rail below starts on a
        // whole pixel row.
        height: layout.topbarHeight,
        borderBottom: 1,
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        backgroundImage: 'none',
        zIndex: (theme) => theme.zIndex.appBar,
      }}
    >
      {/* Toolbar height comes from `mixins.toolbar` in the theme — the same token. */}
      <Toolbar sx={{ gap: 0.75, px: { xs: 1.5, md: 2.5 } }}>
        {/* Only where there is a rail to open: the shell's own pages have none. */}
        {hasSidebar ? (
          <IconButton
            edge="start"
            onClick={onMenuClick}
            aria-label={t('common.expand')}
            sx={{ display: { lg: 'none' }, color: 'text.secondary' }}
          >
            <MenuRoundedIcon />
          </IconButton>
        ) : null}

        <Stack direction="row" alignItems="center" spacing={1.75} sx={{ minWidth: 0 }}>
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }}>
            <Logo height={38} />
          </Box>
          <Box aria-hidden sx={{ display: { xs: 'none', sm: 'block' }, width: '1px', height: 28, bgcolor: 'divider' }} />
          {/* The apps button comes before the switcher (owner's call,
              2026-10-05), so in Arabic it is the one nearer the right edge.
              Order is DOM order — nothing reads the direction, and when the
              button is not drawn the switcher closes up against the rule. */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <AllAppsButton />
            <ModuleSwitcher />
          </Stack>
        </Stack>

        <Box sx={{ flex: 1 }} />

        <NotificationsMenu />
        <OrganizationMenu />

        <ButtonBase
          onClick={(event) => setUserMenu(event.currentTarget)}
          aria-haspopup="menu"
          aria-expanded={Boolean(userMenu)}
          aria-label={t('common.profile')}
          sx={{
            height: 40,
            gap: 1.25,
            borderRadius: '50px',
            px: isPlatform ? 0.75 : 0,
            fontFamily: 'inherit',
            '&:hover': isPlatform ? { bgcolor: 'action.hover' } : undefined,
            '&.Mui-focusVisible': { boxShadow: (th) => `0 0 0 3px ${th.palette.brand.focusRing}` },
          }}
        >
          <Avatar
            src={user?.profilePicture ?? undefined}
            sx={{
              width: 38,
              height: 38,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              fontSize: '0.8125rem',
              // Open, the avatar is ringed like the Shell board's.
              boxShadow: userMenu
                ? (th) => `0 0 0 2px ${th.palette.background.paper}, 0 0 0 4px ${th.palette.brand.borderStrong}`
                : 'none',
            }}
          >
            {initials(user?.fullname)}
          </Avatar>
          {/* Platform staff have no organization menu, so their own name
              stands where the organization's would (Apps board). */}
          {isPlatform ? (
            <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.3, paddingInlineEnd: '4px' }}>
              <Typography noWrap sx={{ fontSize: '0.875rem', fontWeight: 600, maxWidth: 170 }}>
                {user?.fullname ?? '—'}
              </Typography>
              {/* The role's *name* is not on `/auth/me` (backend ask), and a
                  raw code such as PLATFORM_ADMIN is not a label for a reader. */}
              <Typography noWrap sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>
                {t('common.platformTeam')}
              </Typography>
            </Box>
          ) : null}
        </ButtonBase>

        <UserMenu anchor={userMenu} onClose={() => setUserMenu(null)} />
      </Toolbar>
      <RouteBar />
    </AppBar>
  );
}

/**
 * Who you are, the two pages about you, and the two device preferences — the
 * language and the theme — as segmented controls that apply at once.
 */
function UserMenu({ anchor, onClose }: { anchor: HTMLElement | null; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const mode = usePreferencesStore((s) => s.mode);
  const setMode = usePreferencesStore((s) => s.setMode);
  const activeLocale = LOCALES.find((locale) => i18n.language.startsWith(locale)) ?? 'en';
  // Arabic first: the product's default, and the board's order.
  const localeOrder: Locale[] = ['ar', 'en', 'ckb'];

  const go = (to: string) => {
    onClose();
    navigate(to);
  };

  return (
    <Menu
      anchorEl={anchor}
      open={Boolean(anchor)}
      onClose={onClose}
      // Aligned to the avatar's outer edge — the inline end, which a physical
      // origin has to be told.
      anchorOrigin={{ vertical: 'bottom', horizontal: endEdge(theme.direction) }}
      transformOrigin={{ vertical: 'top', horizontal: endEdge(theme.direction) }}
      slotProps={{ paper: { sx: { width: 300, maxWidth: '92vw' } } }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ px: 1.25, pt: 1.25, pb: 1.5 }}>
        <Avatar
          src={user?.profilePicture ?? undefined}
          sx={{ width: 40, height: 40, bgcolor: 'primary.main', color: 'primary.contrastText', fontSize: '0.875rem' }}
        >
          {initials(user?.fullname)}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
            {user?.fullname ?? '—'}
          </Typography>
          {user?.username ? (
            <Typography
              noWrap
              dir="ltr"
              sx={{ fontSize: '0.8125rem', color: 'text.secondary', unicodeBidi: 'isolate', textAlign: 'start' }}
            >
              {user.username}
            </Typography>
          ) : null}
          {/* No role line: only the role's code is known, and a code is not
              a label. It comes back with the role's name (backend ask). */}
        </Box>
      </Stack>
      <Divider sx={{ mx: 0.75, my: '4px !important' }} />
      <MenuItem onClick={() => go(paths.profile)}>
        <ListItemIcon>
          <PersonOutlineRoundedIcon />
        </ListItemIcon>
        {t('common.profile')}
      </MenuItem>
      <MenuItem onClick={() => go(paths.settings)}>
        <ListItemIcon>
          <TuneRoundedIcon />
        </ListItemIcon>
        {t('common.settings')}
      </MenuItem>

      <Segmented id="user-menu-language" icon={<TranslateRoundedIcon />} label={t('common.language')}>
        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          value={activeLocale}
          onChange={(_, next: Locale | null) => next && void i18n.changeLanguage(next)}
          aria-labelledby="user-menu-language"
        >
          {localeOrder.map((locale) => (
            <ToggleButton key={locale} value={locale} lang={locale}>
              {LOCALE_LABELS[locale]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Segmented>
      <Segmented id="user-menu-theme" icon={<ContrastRoundedIcon />} label={t('common.theme')}>
        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          value={mode}
          onChange={(_, next: ThemeMode | null) => next && setMode(next)}
          aria-labelledby="user-menu-theme"
        >
          <ToggleButton value="light">{t('common.light')}</ToggleButton>
          <ToggleButton value="system">{t('common.system')}</ToggleButton>
          <ToggleButton value="dark">{t('common.dark')}</ToggleButton>
        </ToggleButtonGroup>
      </Segmented>

      <Divider sx={{ mx: 0.75, my: '4px !important' }} />
      <MenuItem
        onClick={async () => {
          onClose();
          await logout();
          navigate(paths.login, { replace: true });
        }}
        sx={{ color: 'error.main', fontWeight: 600, '& .MuiListItemIcon-root': { color: 'error.main' } }}
      >
        <ListItemIcon>
          <LogoutRoundedIcon />
        </ListItemIcon>
        {t('common.signOut')}
      </MenuItem>
    </Menu>
  );
}

/** A labelled segmented control inside a menu; not a menu item itself. */
function Segmented({ id, icon, label, children }: { id: string; icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <Box
      // The menu's list navigation would take the arrow keys and the
      // type-ahead letters away from the group's own buttons. Escape and Tab
      // still reach the menu, so it closes as any menu does.
      onKeyDown={(event) => {
        if (event.key !== 'Escape' && event.key !== 'Tab') event.stopPropagation();
      }}
      sx={{ px: 1.25, pt: 1, pb: 0.75, display: 'flex', flexDirection: 'column', gap: 1 }}
    >
      <Box
        id={id}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          fontSize: '0.875rem',
          '& svg': { fontSize: '1.125rem', color: 'text.secondary' },
        }}
      >
        {icon}
        {label}
      </Box>
      <Box sx={{ '& .MuiToggleButton-root': { height: 30, fontSize: '0.8125rem' } }}>{children}</Box>
    </Box>
  );
}
